/**
 * The phone clip recorder's own controls (docs/mobile-editor-layout-spec.md
 * 10.4): the one record/save control, the take clock, the trash, the
 * "Clip saved" toast and the alert that asks before a take is lost. Visual
 * only: every action is a handler the recorder already had.
 */
import { useEffect, type CSSProperties, type ReactNode } from 'react';

/** Spec 10.4: idle, a white ring with a red dot; during a take, a check with "Save" under it. */
export function RecordSaveButton({ recording, size, onRecord, onSave, showLabel = true }: {
  recording: boolean;
  /** The circle's diameter: 64 in portrait, 48 in the landscape rail. */
  size: number;
  onRecord: () => void;
  onSave: () => void;
  /** The "Save" line under the circle; its 16 is reserved while idle so nothing moves. */
  showLabel?: boolean;
}) {
  return (
    <button
      type="button"
      data-testid={recording ? 'save-clip-btn' : 'start-recording-btn'}
      onClick={recording ? onSave : onRecord}
      aria-label={recording ? 'Save clip' : 'Record'}
      title={recording ? 'Save clip' : 'Record'}
      className="relative flex flex-col items-center gap-1 flex-none ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 rounded-full"
    >
      <span
        className="flex items-center justify-center rounded-full"
        style={{ width: size, height: size, border: '3px solid rgba(255,255,255,0.92)', boxSizing: 'border-box' }}
      >
        {recording ? (
          <span className="flex items-center justify-center rounded-full" style={{ width: size - 12, height: size - 12, background: 'var(--color-biscayne)' }}>
            <svg width={Math.round(size * 0.4)} height={Math.round(size * 0.4)} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="5 12.5 10 17.5 19 7" />
            </svg>
          </span>
        ) : (
          <span className="rounded-full" style={{ width: size - 16, height: size - 16, background: 'rgb(239, 68, 68)' }} />
        )}
      </span>
      {showLabel && (
        /* Caption 1 12/16. */
        <span className="text-[12px] leading-[16px] font-semibold text-white" style={{ visibility: recording ? 'visible' : 'hidden' }}>
          Save
        </span>
      )}
    </button>
  );
}

/** A take in progress: a pulsing red dot and the elapsed time. On screen only. */
export function TakeClock({ seconds, glass = false }: { seconds: number; glass?: boolean }) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, '0');
  return (
    <span
      /* Spec 15: times are mono at Subhead 15/20; over the video, the one
         glass style (black at 0.45, 20 blur). */
      className={`flex items-center gap-1.5 flex-none rounded-full px-2.5 font-mono text-[15px] leading-[20px] tabular-nums text-white ${glass ? 'lve-glass' : ''}`}
      style={{ height: 28 }}
      aria-live="polite"
      title="Recording"
    >
      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse motion-reduce:animate-none" />
      {m}:{s}
    </span>
  );
}

/** Discard the take in progress. Asks first (the caller shows the alert). */
export function TrashButton({ onClick, size = 44 }: { onClick: () => void; size?: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Discard this clip"
      title="Discard this clip"
      data-testid="discard-take-btn"
      className={`${REC_BTN} text-[var(--editor-text-muted)]`}
      style={{ width: size, height: size }}
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>
  );
}

/** "Clip saved": a small glass toast over the top of the stage, for 1.6s. */
export function SavedToast({ shownAt, onDone }: { shownAt: number | null; onDone: () => void }) {
  useEffect(() => {
    if (shownAt === null) return;
    const t = setTimeout(onDone, 1600);
    return () => clearTimeout(t);
  }, [shownAt, onDone]);
  if (shownAt === null) return null;
  return (
    <div
      role="status"
      data-testid="clip-saved-toast"
      /* Spec 15: the one glass style over the video. */
      className="absolute left-1/2 -translate-x-1/2 z-[60] pointer-events-none rounded-full px-3 text-[13px] leading-[18px] font-semibold text-white flex items-center gap-1.5 lve-glass"
      style={{ top: 8, height: 30 }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="5 12.5 10 17.5 19 7" /></svg>
      Clip saved
    </div>
  );
}

export interface AlertAction {
  label: string;
  onPress: () => void;
  role?: 'primary' | 'destructive' | 'cancel';
}

/**
 * A centred alert for a choice that must be made before going on (MO-2):
 * a title, then stacked 48 capsule buttons (8.3). Tapping outside is the
 * cancel action, so the gesture is never the one that loses the take (MO-5).
 */
export function RecorderAlert({ title, actions }: { title: string; actions: AlertAction[] }) {
  const cancel = actions.find((a) => a.role === 'cancel');
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center px-4 bg-black/60" onClick={() => cancel?.onPress()}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-[300px] flex flex-col gap-2.5 p-3.5"
        style={{ background: 'var(--editor-surface-raised)', border: '1px solid var(--editor-border)', borderRadius: 26 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Headline 17/22. */}
        <p className="text-center text-[17px] leading-[22px] font-semibold text-white pt-2 pb-3">{title}</p>
        {actions.map((a) => (
          <button
            key={a.label}
            type="button"
            onClick={a.onPress}
            className="h-12 rounded-full text-[17px] leading-[22px] font-medium ios-press focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40"
            style={a.role === 'primary'
              ? { background: 'var(--color-biscayne)', color: '#fff' }
              : { background: 'rgba(120, 120, 128, 0.16)', color: a.role === 'destructive' ? 'rgb(248, 113, 113)' : '#fff' }}
          >
            {a.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Camera bubble on or off (spec 10.6, 10.7). On is a filled biscayne circle, like the pen. */
export function CameraToggle({ on, onToggle, disabled = false, size = 44 }: { on: boolean; onToggle: () => void; disabled?: boolean; size?: number }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={on}
      aria-label={on ? 'Turn camera off' : 'Turn camera on'}
      title={on ? 'Turn camera off' : 'Turn camera on'}
      data-testid="camera-toggle"
      className={REC_BTN}
      style={{ width: size, height: size, ...(on ? { background: 'var(--color-biscayne)', color: '#fff' } : { background: 'transparent', color: 'var(--editor-text-muted)' }) }}
    >
      <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {on ? (
          <><path d="M15.5 10.5l4.6-2.8a.6.6 0 01.9.5v7.6a.6.6 0 01-.9.5l-4.6-2.8" /><rect x="3" y="6.5" width="12.5" height="11" rx="2.25" /></>
        ) : (
          <><path d="M15.5 10.5l4.6-2.8a.6.6 0 01.9.5v7.6a.6.6 0 01-.9.5l-4.6-2.8" /><rect x="3" y="6.5" width="12.5" height="11" rx="2.25" /><path d="M3 3l18 18" /></>
        )}
      </svg>
    </button>
  );
}

/**
 * Spec 15: every recorder control is a 44 circle (36 in a squeezed column)
 * with no fill of its own; the one exception is an active tool, filled
 * biscayne. Press is .ios-press (8.4).
 */
export const REC_BTN = 'relative flex-none rounded-full inline-flex items-center justify-center ios-press disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40';

/** A recorder icon button (spec 15): 44 round, transparent, or biscayne when active. */
export function RecButton({ label, onClick, size = 44, height = size, active = false, activeColor = '#fff', color = 'var(--editor-text-muted)', disabled = false, pressed, testId, children }: {
  label: string;
  onClick: () => void;
  size?: number;
  /** Side by side rows (15.20): 36 wide in a 44 row. */
  height?: number;
  active?: boolean;
  /** The icon's colour while active (the marker shows its pen colour). */
  activeColor?: string;
  color?: string;
  disabled?: boolean;
  pressed?: boolean;
  testId?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      data-testid={testId}
      className={REC_BTN}
      style={{
        width: size,
        height,
        background: active ? 'var(--color-biscayne)' : 'transparent',
        color: active ? activeColor : color,
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
      }}
    >
      {children}
    </button>
  );
}

/** Playback speed (spec 15): "1×" at Subhead 15/20 semibold in a 44 target, no chip behind it. */
export function SpeedButton({ speed, onCycle, disabled = false, size = 44, height = size, which }: {
  speed: number;
  onCycle: () => void;
  disabled?: boolean;
  size?: number;
  /** Side by side rows (15.20): a 36 wide chip in a 44 row. */
  height?: number;
  /** Side by side: which video it belongs to, for the label. */
  which?: string;
}) {
  return (
    <button
      type="button"
      onClick={onCycle}
      disabled={disabled}
      aria-label={`${which ? `${which} video, ` : ''}playback speed ${speed}x, tap to change`}
      className={`${REC_BTN} text-[15px] leading-[20px] font-semibold tabular-nums`}
      style={{ width: size, height, color: 'var(--editor-text)' }}
    >
      {speed}×
    </button>
  );
}

/** Close the recorder (spec 15.7): an X in its own 44 glass pill. Back to the lesson; asks during a take. */
export function CloseX({ onClick, style }: { onClick: () => void; style?: CSSProperties }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close recorder"
      title="Close recorder"
      data-testid="recorder-close"
      className={`${REC_BTN} lve-glass`}
      style={{ width: 44, height: 44, ...style }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  );
}

/** The mute glyph: a speaker with waves, or struck through when muted. */
export function MuteGlyph({ muted, className }: { muted: boolean; className: string }) {
  return muted ? (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" /></svg>
  ) : (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" /></svg>
  );
}

/**
 * The collapse chevron at the top of a landscape column (spec 15.14): 28
 * tall inside the pill, its target 44. Up while the columns are open, down
 * while they are collapsed. Either column's chevron collapses or opens both.
 */
export function ColumnChevron({ collapsed, onToggle, width }: { collapsed: boolean; onToggle: () => void; width: number }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Show tools' : 'Hide tools'}
      title={collapsed ? 'Show tools' : 'Hide tools'}
      data-testid="column-chevron"
      className={`${REC_BTN} after:absolute after:content-[''] after:-inset-y-2 after:inset-x-0`}
      style={{ width, height: 28, color: 'var(--editor-text)' }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {collapsed ? <polyline points="6 9 12 15 18 9" /> : <polyline points="6 15 12 9 18 15" />}
      </svg>
    </button>
  );
}
